import { PublishingPauseDialogs, PublishingPausedBanner } from '@/shared/components/PublishingPause';
import { AccountBillingBanner } from '@/shared/components/billing/AccountBillingBanner';
import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Sidebar } from './Sidebar';
import { useNavStore } from '@/shared/stores/nav.store';
import { MobileNav } from './MobileNav';
import { Topbar } from './Topbar';
import { CommandPalette } from '@/shared/components/CommandPalette';
import { ConsentBar } from '@/shared/components/ConsentBar';
import { ROUTES } from '@/shared/constants/routes';
import { pageVariants } from '@/shared/constants/motion';
import { useWorkspace } from '@/shared/hooks/useWorkspace';
import { useAuthStore } from '@/shared/stores/auth.store';
import { markOnboardingOffered, shouldOfferOnboarding } from '@/shared/lib/onboarding-gate';
import { useAppliedBranding, useBrandFavicon } from '@/shared/hooks/useBranding';
import { useSyncUiLanguage } from '@/shared/hooks/useSyncUiLanguage';
import { useFormattingRegion } from '@/shared/hooks/useFormattingRegion';
import { ReceiptHost } from '@/shared/components/ReceiptHost';
import { MessageAlerts } from '@/shared/components/MessageAlerts';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import { DESKTOP_QUERY, useMatchMedia } from '@/shared/hooks/useMatchMedia';
import { isDialogOpen, PALETTE_LAYER } from '@/shared/hooks/useDialogLayer';


/** Screens that show the paused-publishing banner: Overview, Calendar, Composer. */
const PAUSE_BANNER_ROUTES: readonly string[] = [ROUTES.OVERVIEW, ROUTES.CALENDAR, ROUTES.COMPOSER];
export function AppLayout() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isDesktop = useMatchMedia(DESKTOP_QUERY);
  const reduced = useReducedMotion() ?? false;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // From 1280px the rail docks with its labels unless collapsed (DS04).
  const navCollapsed = useNavStore((st) => st.collapsed);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Routes that behave as a wide workspace rather than a reading-width page.
  const isWorkspaceRoute =
    pathname.startsWith(ROUTES.COMPOSER) || pathname.startsWith(ROUTES.INBOX) || pathname.startsWith(ROUTES.CALENDAR);

  // Every "compose / new post" entry point in the app goes through ONE flow:
  // navigate to the /composer route.
  const goToCompose = (): void => {
    void navigate(ROUTES.COMPOSER);
  };

  // Initialize workspace context early — ensures X-Workspace-Id header is available for all child routes
  const { workspaces, currentWorkspace } = useWorkspace();
  const userId = useAuthStore((s) => s.user?.id);
  // Emails are written in the language on screen (user_metadata.ui_language).
  useSyncUiLanguage();
  // Dates in the brand's country's order when the language carries no region (en → en-IN).
  useFormattingRegion();

  // A brand-new owner lands in the onboarding wizard (S03) once, whichever
  // way they signed in (password, magic link, Google).
  useEffect(() => {
    if (!currentWorkspace || !shouldOfferOnboarding(workspaces ?? [], currentWorkspace, userId)) return;
    markOnboardingOffered(currentWorkspace.id);
    void navigate(ROUTES.ONBOARDING, { replace: true });
  }, [workspaces, currentWorkspace, userId, navigate]);

  // White label (Agency): the workspace's logo, favicon and Powered-by
  // setting. Our chrome colours stay; see useBranding for why.
  const branding = useAppliedBranding();
  useBrandFavicon(branding.faviconUrl);

  // Cmd+K / Ctrl+K opens the command palette
  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        // Not over an open modal or sheet: the palette (z-200) opened behind
        // it and took its arrow keys. When the palette is the open layer,
        // Cmd+K closes it.
        setPaletteOpen((o) => (o ? false : !isDialogOpen(PALETTE_LAYER)));
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // The drawer never survives a route change.
  useEffect(() => {
    setSidebarOpen(false);
  }, [pathname]);

  return (
    <div className="min-h-screen bg-[var(--color-bg)]">
      {/* Skip to main content link — WCAG 2.4.1 */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:start-2 focus:z-[100] focus:bg-[var(--ink)] focus:text-white focus:py-2 focus:px-4 focus:rounded-lg focus:text-ui focus:font-semibold"
      >
        {t('common.skipToContent')}
      </a>

      <Topbar
        brandLogoUrl={branding.logoUrl}
        menuOpen={sidebarOpen}
        onMenuToggle={() => setSidebarOpen((o) => !o)}
        onNewPost={goToCompose}
        onCommandPalette={() => setPaletteOpen(true)}
      />

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onCompose={() => {
          setPaletteOpen(false);
          goToCompose();
        }}
      />

      {/* Drawer scrim (mobile/tablet) */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-[var(--scrim)] z-40 lg:hidden"
          role="presentation"
          aria-hidden="true"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onCompose={goToCompose}
        onSearch={() => setPaletteOpen(true)}
        showPoweredBy={branding.applied && branding.showPoweredBy}
      />

      {/* Mounted only below lg: hidden by CSS it still ran its badge queries
          on every desktop page. */}
      {!isDesktop && <MobileNav onMore={() => setSidebarOpen(true)} />}

      {/* Asked once per consent version; renders nothing once chosen. */}
      <ConsentBar aboveTabBar />

      {/* Main content area.
          ONE page scrollbar, always. Side columns hold their position with
          `position: sticky` instead of nested scroll containers. */}
      <main
        id="main-content"
        className={['lg:ms-[72px] pt-[64px] pb-[88px] lg:pb-0 min-h-screen min-w-0', navCollapsed ? '' : 'xl:ms-[232px]'].join(' ')}
        tabIndex={-1}
      >
        {/* overflow-x-CLIP, not hidden: `hidden` creates a scroll container,
            which silently breaks `position: sticky` for everything inside. */}
        {/* A size container (@container): pages lay out their columns by the
            room they actually have (the window minus the 72 or 232 px rail
            and the gutters) with @min-[...] variants, not by the window. */}
        <div
          className={`@container mx-auto min-w-0 overflow-x-clip ${isWorkspaceRoute ? 'max-w-[1500px]' : 'max-w-[1280px]'}`}
          style={{ padding: 'var(--gutter)' }}
        >
          {/* Route change: content fades in from 6px below (`page` timing).
              Keyed on the pathname so each route runs it once; no exit
              animation, so navigation never waits on motion. */}
          {/* Publishing paused for the brand (Task 1.5): where posts are planned and written. */}
          {/* A failed renewal, or what the plan paused (AL-11, D-01): the owner, on every screen. */}
          <AccountBillingBanner className="mb-4" />
          {PAUSE_BANNER_ROUTES.some((r) => pathname.startsWith(r)) && <PublishingPausedBanner className="mb-4" />}
          <motion.div key={pathname} variants={pageVariants(reduced)} initial="initial" animate="animate">
            {/* One page that throws no longer takes the shell with it, and
                navigating away (a new key) clears the error. App.tsx keeps
                the top-level boundary as the last resort. */}
            <ErrorBoundary key={pathname}>
              <Outlet />
            </ErrorBoundary>
          </motion.div>
        </div>
      </main>
      {/* A post's receipt, from any screen or a ?receipt= link (Task 1.3). */}
      <ReceiptHost />
      <MessageAlerts workspaceId={currentWorkspace?.id} />
      {/* Pause and resume publishing, opened from any screen (Task 1.5). */}
      <PublishingPauseDialogs />
    </div>
  );
}
